import { useEffect, useRef, useState } from "react";

// Keep keystrokes (and IME composition) local to the input, and hand the text
// to the heavy page only after typing pauses or composition ends. Otherwise
// every key re-renders the whole editor and its A4 preview.
export default function DeferredSearchInput({ value, onCommit, delay = 220, ...props }) {
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  const composing = useRef(false);
  const timer = useRef(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // Follow outside changes such as clearing the filters or loading a draft.
  useEffect(() => {
    if (value === committed.current) return;
    committed.current = value;
    window.clearTimeout(timer.current);
    setDraft(value);
  }, [value]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const commit = (next) => {
    window.clearTimeout(timer.current);
    if (next === committed.current) return;
    committed.current = next;
    commitRef.current(next);
  };
  const schedule = (next) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => commit(next), delay);
  };

  return (
    <input
      {...props}
      value={draft}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        if (!composing.current && !event.nativeEvent?.isComposing) schedule(next);
      }}
      onCompositionStart={(event) => {
        composing.current = true;
        window.clearTimeout(timer.current);
        props.onCompositionStart?.(event);
      }}
      onCompositionEnd={(event) => {
        composing.current = false;
        schedule(event.currentTarget.value);
        props.onCompositionEnd?.(event);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.nativeEvent?.isComposing) commit(event.currentTarget.value);
        props.onKeyDown?.(event);
      }}
      onBlur={(event) => {
        if (!composing.current) commit(event.currentTarget.value);
        props.onBlur?.(event);
      }}
    />
  );
}
